<!-- SEO landing page (target: "intelligent character recognition", ~9.9K/mo, KD ~3 —
     highest-volume near-zero-difficulty topical-authority anchor). -->
<template>
  <SeoLanding
    kicker="Guide"
    h1="ICR vs OCR: What's the Difference?"
    subtitle="The difference in one line: OCR reads printed text by matching fixed character shapes, while ICR (Intelligent Character Recognition) uses machine learning to read handwriting and variable fonts. Below: how each works, examples of where they fit, and the document-AI step that turns either into usable data."
    cta-label="Try document AI free"
    cta-to="/signup"
    :sections="sections"
    :faqs="faqs"
    closing-title="From characters to structured data"
    closing-subtitle="Papersnap reads your documents and returns labeled fields, not just text. Free for 5 pages a month."
    :related-links="relatedLinks"
  />
</template>

<script setup>
import SeoLanding from '@/components/marketing/SeoLanding.vue'
import { usePageSeo, faqJsonLd, articleJsonLd } from '@/composables/usePageSeo'

const CANONICAL = 'https://www.papersnap.io/intelligent-character-recognition'

const sections = [
  {
    h2: 'ICR vs OCR: what is the difference?',
    paragraphs: [
      'Optical Character Recognition (OCR) converts an image of text into machine-readable characters. It excels at printed text — a typed invoice, a book page, a printed receipt. Intelligent Character Recognition (ICR) extends OCR to handle handwriting and variable, unconstrained text: hand-filled forms, signatures, and the messier inputs OCR alone struggles with.',
      'The practical difference is adaptability. OCR recognizes fixed character shapes; ICR uses machine learning to interpret many handwriting styles and improve over time. Both, though, still stop at "here are the characters." The step businesses actually need is understanding what those characters mean — which field is the invoice total, which line is a date.',
    ],
  },
  {
    h2: 'Beyond ICR: document intelligence',
    paragraphs: [
      'Modern document AI adds a layer on top of OCR/ICR: it classifies the document (is this an invoice, a receipt, a utility bill?) and extracts labeled fields, not just raw text. Instead of a string of characters, you get "invoice_number: 4471, total: 2,318.40, due_date: 2026-08-01" — data you can use immediately.',
      'That is the difference between reading a document and understanding it. OCR and ICR are the reading step; document intelligence is the understanding step, and it is what turns a scanned page into a spreadsheet row or an API payload.',
    ],
  },
  {
    h2: 'Where each fits',
    bullets: [
      { label: 'OCR', text: 'printed text — typed invoices, printed receipts, book pages' },
      { label: 'ICR', text: 'handwriting and variable text — hand-filled forms, notes' },
      { label: 'Document AI', text: 'classification + labeled field extraction — the business-ready output' },
      { label: 'Papersnap', text: 'combines OCR for the reading with document AI for the understanding, so you get structured JSON/CSV' },
    ],
  },
]

const faqs = [
  { q: 'What is intelligent character recognition?', a: 'ICR is a form of OCR that recognizes handwriting and variable text using machine learning. Where OCR handles printed characters, ICR interprets many handwriting styles and improves over time — useful for hand-filled forms.' },
  { q: 'What is the difference between OCR and ICR?', a: 'OCR recognizes printed, fixed-shape characters; ICR extends that to handwriting and unconstrained text. Both output characters — turning those characters into labeled fields (the invoice total, the date) is the job of document intelligence.' },
  { q: 'Do I need ICR or document intelligence?', a: 'If you only need the characters off a page, OCR/ICR is enough. If you need usable data — specific fields pulled from invoices, receipts, or statements — you want document intelligence, which classifies the document and extracts labeled fields. That is what Papersnap does.' },
  { q: 'Does Papersnap use ICR?', a: 'Papersnap uses OCR to read documents and document AI to understand them — classifying the document type and extracting labeled fields as JSON or CSV, rather than returning raw characters.' },
]

const relatedLinks = [
  { to: '/receipt-scanner', label: 'Receipt scanner' },
  { to: '/invoice-ocr', label: 'Invoice OCR' },
  { to: '/pdf-to-csv', label: 'PDF to CSV' },
]

usePageSeo({
  title: 'ICR vs OCR — The Difference Explained (With Examples)',
  description: 'ICR reads handwriting via machine learning; OCR reads printed text. See the difference with examples — then extract labeled data from documents free.',
  path: '/intelligent-character-recognition',
  jsonLd: [
    articleJsonLd({ headline: 'ICR vs OCR — The Difference Explained (With Examples)', description: 'How ICR differs from OCR and where document intelligence fits.', url: CANONICAL }),
    faqJsonLd(faqs.map((f) => ({ question: f.q, answer: f.a }))),
  ],
})
</script>
